import React, { useEffect, useState, useMemo } from "react";
import {
  Box,
  Button,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  TextField,
  Typography,
  IconButton,
  Autocomplete,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { toast } from "react-toastify";
import { updateLearner, getAllGroups } from "../../../services/adminServices/CoeServices";
import { getAllCoeLabs } from "../../../services/mentorServices/DailyPracticeServices";
import { updateLearnerMentor } from "../../../services/mentorServices/MentorServices";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "60%",
  maxHeight: "98vh",
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: "8px",
  outline: "none",
  overflowY: "auto",
};

const EditStudentModal = ({ open, student, onClose, fetchData, coeId, isMentorView = false }) => {
  const [formValues, setFormValues] = useState({});
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [labOptions, setLabOptions] = useState([]);
  const [labLoading, setLabLoading] = useState(false);
  const [groupOptions, setGroupOptions] = useState([]);
  const [groupLoading, setGroupLoading] = useState(false);
  const [isCustomGroup, setIsCustomGroup] = useState(false);

  const activeCoeId = coeId || student?.coeCenter?.id || student?.coe_center_id || student?.coe_id || student?.coeId;

  useEffect(() => {
    if (open) {
      setGroupLoading(true);
      getAllGroups()
        .then((res) => {
          setGroupOptions(res?.data?.data || []);
        })
        .catch(() => setGroupOptions([]))
        .finally(() => setGroupLoading(false));
    }
  }, [open]);

  useEffect(() => {
    if (open && activeCoeId) {
      setLabLoading(true);
      getAllCoeLabs(activeCoeId)
        .then((res) => {
          const labs = res?.data?.data || [];
          setLabOptions(Array.isArray(labs) ? labs : []);
        })
        .catch(() => setLabOptions([]))
        .finally(() => setLabLoading(false));
    } else if (open && !activeCoeId) {
      setLabLoading(false);
      setLabOptions([]);
    }
  }, [open, activeCoeId]);

  useEffect(() => {
    if (student) {
      const initialLabId = student.lab?.id || student.lab_id || student.labId || "";
      const initialLabName = student.lab?.lab_name || student.lab_name || student.labname || student.labName || "";

      setFormValues({
        ...student,
        email: student.email || student.personal_email || student.personalEmail || "",
        institution_email: student.institution_email || student.institutionEmail || "",
        lab_id: initialLabId,
        lab_name: initialLabName,
        groupName: student.groupName || student.group_name || "",
      });
    }
  }, [student]);

  const combinedGroupOptions = useMemo(() => {
    const current = formValues.groupName?.trim();
    if (current && !groupOptions.includes(current)) {
      return [current, ...groupOptions];
    }
    return groupOptions;
  }, [groupOptions, formValues.groupName]);

  useEffect(() => {
    if (labOptions.length > 0) {
      setFormValues((prev) => {
        const currentLabId = prev.lab_id;
        const currentLabName = prev.lab_name || prev.labname || prev.labName;

        const matched = labOptions.find(
          (l) =>
            (currentLabId && (l.id === currentLabId || l.lab_id === currentLabId)) ||
            (currentLabName && (l.lab_name || l.name) === currentLabName)
        );

        if (matched) {
          return {
            ...prev,
            lab_id: matched.id || matched.lab_id || prev.lab_id,
            lab_name: matched.lab_name || matched.name || prev.lab_name,
          };
        }
        return prev;
      });
    }
  }, [labOptions]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormValues((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleLabChange = (event) => {
    const selectedLabId = event.target.value;
    const matchedLab = labOptions.find(
      (l) => l.id === selectedLabId || l.lab_id === selectedLabId
    );

    setFormValues((prev) => ({
      ...prev,
      lab_id: selectedLabId,
      lab_name: matchedLab ? (matchedLab.lab_name || matchedLab.name) : prev.lab_name,
    }));

    if (errors.lab_id || errors.lab_name) {
      setErrors((prev) => ({ ...prev, lab_id: "", lab_name: "" }));
    }
  };

  const validateFields = () => {
    const newErrors = {};

    // Name: Required (3 to 100 characters)
    const trimmedName = formValues.name?.trim() || "";
    if (!trimmedName) {
      newErrors.name = "Name is required";
    } else if (trimmedName.length < 3 || trimmedName.length > 100) {
      newErrors.name = "Name must be between 3 and 100 characters";
    }

    // Group Name: Required
    if (!formValues.groupName?.trim()) {
      newErrors.groupName = "Group name is required";
    }

    // Lab Name: Required
    if (!formValues.lab_id && !formValues.lab_name) {
      newErrors.lab_id = "Lab Name is required";
    }

    // Email (Personal): Required
    const trimmedEmail = formValues.email?.trim() || "";
    if (!trimmedEmail) {
      newErrors.email = "Personal Email (Gmail) is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      newErrors.email = "Enter a valid email (e.g., user@gmail.com)";
    }

    // Institution Email: Required (Valid email format)
    const trimmedInstEmail = formValues.institution_email?.trim() || "";
    if (!trimmedInstEmail) {
      newErrors.institution_email = "Institution email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedInstEmail)) {
      newErrors.institution_email = "Enter a valid institution email format";
    }

    // Roll Number: Required
    if (!formValues.roll_num?.toString().trim()) {
      newErrors.roll_num = "Roll number is required";
    }

    // Passout Year: Required (>= 1900)
    const passoutStr = formValues.passout_year?.toString().trim() || "";
    const passoutNum = parseInt(passoutStr, 10);
    if (!passoutStr) {
      newErrors.passout_year = "Passout year is required";
    } else if (!/^\d{4}$/.test(passoutStr) || isNaN(passoutNum) || passoutNum < 1900) {
      newErrors.passout_year = "Passout year must be a valid 4-digit year >= 1900";
    }

    // Enrolment Year: Required (>= 1900)
    const enrolStr = formValues.enrolment_year?.toString().trim() || "";
    const enrolNum = parseInt(enrolStr, 10);
    if (!enrolStr) {
      newErrors.enrolment_year = "Enrolment year is required";
    } else if (!/^\d{4}$/.test(enrolStr) || isNaN(enrolNum) || enrolNum < 1900) {
      newErrors.enrolment_year = "Enrolment year must be a valid 4-digit year >= 1900";
    }

    // Current Semester: Required (1 to 8)
    const semStr = formValues.current_semester?.toString().trim() || "";
    const semNum = parseInt(semStr, 10);
    if (!semStr) {
      newErrors.current_semester = "Current semester is required";
    } else if (isNaN(semNum) || semNum < 1 || semNum > 8) {
      newErrors.current_semester = "Semester must be a number between 1 and 8";
    }

    // Mobile: Optional (10 digits starting with 6,7,8,9)
    const trimmedMobile = formValues.mobile?.toString().trim() || "";
    if (trimmedMobile && !/^[6-9]\d{9}$/.test(trimmedMobile)) {
      newErrors.mobile = "Mobile number must be a 10-digit number starting with 6, 7, 8, or 9";
    }

    // Device ID: Optional (max 100)
    const trimmedDevice = formValues.device_id?.toString().trim() || "";
    if (trimmedDevice && trimmedDevice.length > 100) {
      newErrors.device_id = "Device ID cannot exceed 100 characters";
    }

    // Stream: Required (max 100)
    const trimmedStream = formValues.stream?.toString().trim() || "";
    if (!trimmedStream) {
      newErrors.stream = "Stream is required";
    } else if (trimmedStream.length > 100) {
      newErrors.stream = "Stream cannot exceed 100 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateFields()) return;
    setLoading(true);
    try {
      const learnerId = student.learner_id || student.id;

      if (isMentorView) {
        const mentorPayload = {
          name: formValues.name.trim(),
          email: formValues.email.trim(),
          roll_num: formValues.roll_num.toString().trim(),
        };

        if (formValues.mobile?.toString().trim()) {
          mentorPayload.mobile = formValues.mobile.toString().trim();
        }
        if (formValues.stream?.toString().trim()) {
          mentorPayload.stream = formValues.stream.toString().trim();
        }
        if (formValues.passout_year) {
          mentorPayload.passout_year = parseInt(formValues.passout_year, 10) || undefined;
        }
        if (formValues.groupName?.trim()) {
          mentorPayload.groupName = formValues.groupName.trim();
        }
        if (formValues.institution_email?.trim()) {
          mentorPayload.institution_email = formValues.institution_email.trim();
        }
        if (formValues.enrolment_year) {
          mentorPayload.enrolment_year = parseInt(formValues.enrolment_year, 10) || undefined;
        }
        if (formValues.current_semester) {
          mentorPayload.current_semester = parseInt(formValues.current_semester, 10) || undefined;
        }

        const response = await updateLearnerMentor(learnerId, mentorPayload);
        toast.success(response?.data?.message || "Learner details updated successfully!");
      } else {
        const obj = {
          name: formValues.name.trim(),
          email: formValues.email.trim(),
          institution_email: formValues.institution_email.trim(),
          coe_center_id: formValues.coe_center_id || activeCoeId,
          lab_id: parseInt(formValues.lab_id, 10) || 0,
          lab_name: formValues.lab_name,
          roll_num: formValues.roll_num.toString().trim(),
          passout_year: parseInt(formValues.passout_year, 10) || 0,
          stream: formValues.stream.toString().trim(),
          groupName: formValues.groupName.trim(),
          enrolment_year: parseInt(formValues.enrolment_year, 10) || 0,
          current_semester: parseInt(formValues.current_semester, 10) || 0,
          mobile: formValues.mobile?.toString().trim() || "",
        };

        if (formValues.device_id?.toString().trim()) {
          obj.device_id = formValues.device_id.toString().trim();
        }

        const response = await updateLearner(learnerId, obj);
        toast.success(response?.data?.message || "Learner details updated successfully!");
      }

      fetchData();
      onClose();
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || error?.error);
    } finally {
      setLoading(false);
    }
  };

  const fields = [
    { label: "Name", name: "name", required: true, inputProps: { minLength: 3, maxLength: 100 } },
    { label: "Group Name", name: "groupName", required: true },
    {
      label: "Institution Email",
      name: "institution_email",
      required: true,
      type: "email",
    },
    { label: "Personal Gmail", name: "email", required: true, type: "email" },
    { label: "Roll Number", name: "roll_num", required: true, disabled: isMentorView },
    { label: "Enrolment Year", name: "enrolment_year", required: true, type: "number", inputProps: { min: 1900 } },
    { label: "Current Semester (1-8)", name: "current_semester", required: true, type: "number", inputProps: { min: 1, max: 8 } },
    { label: "Passout Year", name: "passout_year", required: true, type: "number", inputProps: { min: 1900 } },
    { label: "Stream", name: "stream", required: true, inputProps: { maxLength: 100 } },
    { label: "Mobile Number (Optional)", name: "mobile", inputProps: { maxLength: 10 } },
    { label: "Device ID (Optional)", name: "device_id", inputProps: { maxLength: 100 } },
  ];

  const renderTextField = ({ label, name, required, type = "text", inputProps, helperText, disabled }) => (
    <TextField
      key={name}
      size="small"
      label={label}
      name={name}
      type={type}
      value={formValues[name] || ""}
      onChange={handleChange}
      error={!!errors[name]}
      helperText={errors[name] || helperText}
      required={required}
      disabled={loading || disabled}
      fullWidth
      inputProps={inputProps}
    />
  );

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 2 }}>
          Edit Student Details
        </Typography>
        <Box
          component="form"
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
          }}
        >
          {/* Name */}
          {renderTextField(fields[0])}

          {/* Group Name Autocomplete & Custom Input Toggle */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {!isCustomGroup ? (
              <Autocomplete
                options={combinedGroupOptions}
                loading={groupLoading}
                sx={{ width: "100%" }}
                value={formValues.groupName || ""}
                onChange={(event, newValue) => {
                  setFormValues((prev) => ({ ...prev, groupName: newValue || "" }));
                  if (errors.groupName) {
                    setErrors((prev) => ({ ...prev, groupName: "" }));
                  }
                }}
                disabled={loading}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Select Group Name"
                    error={!!errors.groupName}
                    helperText={errors.groupName}
                    required
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {groupLoading ? <CircularProgress color="inherit" size={18} sx={{ mr: 1 }} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                value={formValues.groupName || ""}
                onChange={(e) => {
                  const val = e.target.value;
                  setFormValues((prev) => ({ ...prev, groupName: val }));
                  if (errors.groupName) {
                    setErrors((prev) => ({ ...prev, groupName: "" }));
                  }
                }}
                label="Enter Group Name"
                error={!!errors.groupName}
                helperText={errors.groupName}
                required
                disabled={loading}
              />
            )}
            <IconButton
              onClick={() => setIsCustomGroup(!isCustomGroup)}
              disabled={loading}
              title={
                isCustomGroup ? "Select existing group" : "Enter new group"
              }
            >
              {isCustomGroup ? (
                <RemoveIcon color="error" />
              ) : (
                <AddIcon color="primary" />
              )}
            </IconButton>
          </Box>

          {/* Lab Name Select tag */}
          <FormControl fullWidth size="small" error={!!errors.lab_id} required>
            <InputLabel id="edit-student-lab-label">Lab Name</InputLabel>
            <Select
              labelId="edit-student-lab-label"
              id="edit-student-lab-select"
              name="lab_id"
              value={formValues.lab_id || ""}
              onChange={handleLabChange}
              label="Lab Name"
              disabled={loading || isMentorView || labLoading}
              IconComponent={labLoading ? () => (
                <Box sx={{ display: "flex", alignItems: "center", mr: 1.5, pointerEvents: "none" }}>
                  <CircularProgress size={18} thickness={4} />
                </Box>
              ) : undefined}
              renderValue={(selected) => {
                if (labLoading) {
                  return (
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1, color: "text.secondary" }}>
                      <span>{formValues.lab_name || "Loading labs..."}</span>
                    </Box>
                  );
                }
                const matched = labOptions.find(
                  (l) => (l.id || l.lab_id) === selected || (l.lab_name || l.name) === selected
                );
                return matched ? (matched.lab_name || matched.name) : (formValues.lab_name || selected || "");
              }}
            >
              {labLoading ? (
                <MenuItem value="" disabled>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <CircularProgress size={16} />
                    <Typography variant="body2" sx={{ color: "text.secondary" }}>
                      Loading labs...
                    </Typography>
                  </Box>
                </MenuItem>
              ) : labOptions.length > 0 ? (
                labOptions.map((lab, index) => {
                  const lId = lab.id || lab.lab_id;
                  const lName = lab.lab_name || lab.name || "";
                  return (
                    <MenuItem key={lId || index} value={lId}>
                      {lName}
                    </MenuItem>
                  );
                })
              ) : (
                <MenuItem value="" disabled>
                  No labs found
                </MenuItem>
              )}
              {formValues.lab_id && !labOptions.some((l) => (l.id || l.lab_id) === formValues.lab_id) && (
                <MenuItem value={formValues.lab_id} sx={{ display: "none" }}>
                  {formValues.lab_name || formValues.lab_id}
                </MenuItem>
              )}
            </Select>
            {errors.lab_id ? (
              <FormHelperText>{errors.lab_id}</FormHelperText>
            ) : labLoading ? (
              <FormHelperText sx={{ color: "text.secondary" }}>Fetching lab options...</FormHelperText>
            ) : null}
          </FormControl>

          {fields.slice(2).map(renderTextField)}

          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button variant="contained" onClick={handleSubmit} disabled={loading}>
              {loading ? "Saving..." : "Save"}
            </Button>
            <Button variant="outlined" onClick={onClose} disabled={loading}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default EditStudentModal;
